import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { PlusIcon, TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { formatRoleName, formatPermissionName } from 'utils';
import ButtonComponent from 'components/common/ButtonComponent';
import TableComponent from 'components/common/TableComponent';
import UserModal from 'components/modals/UserModal';
import ActionCardComponent from 'components/common/ActionCardComponent';
import SectionIntro from 'components/common/SectionIntro';
import { makeRequest } from 'services/request.service';
import { useAuth } from 'auth/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Users() {
  const { t } = useTranslation();
  const hasFetched = useRef(false);
  const { user } = useAuth();
  const [state, setState] = useState({
    usersWithPermissions: [],
    positionsRoles: [],
    permissionsList: [],
    searchTerm: '',
    isOpenPermissionModal: false,
    loading: true,
    error: null,
    refresh: 0,
    selectedUser: {}
  });

  const getUserWithPermissions = useCallback(async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/permissions/users/${user.farmId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      });

      setState(s => {
        const { positionsRoles, permissionsList } = s;

        const usersWithPermissions = response.data
          .filter(u => (user.roleId === 1 ? true : u.roleId !== 1))
          .map(user => {
            const matchedRole = positionsRoles.find(
              role => parseInt(role.roleId) === parseInt(user.roleId)
            );
            const position = matchedRole ? formatRoleName(matchedRole.roleName) : 'Unknown';

            const matchedPermissions = user.permissions.length
              ? user.permissions
                  .map(permissionId =>
                    permissionsList.find(permission => permission.permissionId === permissionId)
                  )
                  .filter(Boolean)
              : [];

            const permissions = matchedPermissions.length
              ? matchedPermissions.map(p => (p.name ? formatPermissionName(p.name) : 'Unknown'))
              : ['Full Access'];

            const fullName = `${user.firstName} ${user.lastName}`.trim();

            return { ...user, position, permissions, fullName };
          });

        return { ...s, usersWithPermissions };
      });
    } catch (err) {
      setState(s => ({
        ...s,
        error: t('users.failedToLoadUsers'),
        loading: false,
        usersWithPermissions: []
      }));
    }
  }, [t, user.farmId, user.roleId]);

  const getPermissions = useCallback(async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: '/management/permissions',
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      });

      setState(s => ({
        ...s,
        permissionsList: Array.isArray(response.data) ? response.data : []
      }));
    } catch (err) {
      setState(s => ({
        ...s,
        error: t('users.failedToLoadPermissions'),
        loading: false,
        permissionsList: []
      }));
    }
  }, [t]);

  const getPositionsRoles = useCallback(async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: '/management/permissions/roles',
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      });

      const roles = Array.isArray(response.data) ? response.data : [];

      setState(s => ({
        ...s,
        positionsRoles: roles
      }));
    } catch (err) {
      setState(s => ({
        ...s,
        error: t('users.failedToLoadRoles'),
        loading: false,
        positionsRoles: []
      }));
    }
  }, [t]);

  // filters by email or position
  const filteredUsers = state.usersWithPermissions.filter(
    user =>
      user.email.toLowerCase().includes(state.searchTerm) ||
      user.position.toLowerCase().includes(state.searchTerm)
  );

  const permissionsTableData = {
    columns: [
      // { key: 'fullName', label: 'Full Name' },
      { key: 'email', label: t('form.email') },
      { key: 'position', label: t('users.position') },
      { key: 'permissions', label: t('users.managementAccess') },
      {
        key: 'actions',
        label: t('common.manage'),
        cell: (value, user) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEdit(user)}
              title={t('common.edit')}
              className="p-1 rounded transition"
            >
              <PencilSquareIcon className="w-5 h-5 text-blue-500" />
            </button>
            <button
              onClick={() => handleDelete(user)}
              title={t('common.delete')}
              className="p-1 rounded transition"
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )
      }
    ],
    rows: filteredUsers
  };

  const handleCreateUpdateUserPermission = async formData => {
    try {
      console.warn('state.selectedUser:', state.selectedUser);
      const isEditing = !!state.selectedUser;
      const userId = state.selectedUser?.userId;
      const url = isEditing
        ? `/management/permissions/update-user/${userId}`
        : `/management/permissions/add-user/${user.farmId}`;

      const payload = {
        // firstName: formData.firstName,
        // lastName: formData.lastName,
        email: formData.email,
        roleId: formData.roleId,
        permissions: formData.permissions,
        ...(formData.password && { password: formData.password })
      };

      const response = await makeRequest({
        method: isEditing ? 'PUT' : 'POST',
        url,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        data: payload
      });

      if (![200, 201].includes(response.status)) return;

      const formatPermissions = permissionIds =>
        state.permissionsList
          .filter(p => permissionIds.includes(p.permissionId))
          .map(p => formatPermissionName(p.name));

      const sanitizedUser = {
        userId: response.data.userId,
        // firstName: formData.firstName,
        // lastName: formData.lastName,
        email: formData.email,
        roleId: formData.roleId,
        permissions: formatPermissions(response.data.permissions || formData.permissions || [])
      };

      setState(s => {
        const updatedUsers = isEditing
          ? s.usersWithPermissions.map(user => (user.userId === userId ? sanitizedUser : user))
          : [sanitizedUser, ...s.usersWithPermissions];

        return {
          ...s,
          usersWithPermissions: updatedUsers,
          isOpenPermissionModal: false,
          selectedUser: null
        };
      });
    } catch (err) {
      console.error('Error creating/updating user permission:', err);
    }
  };

  const handleEdit = row => {
    const selected = {
      userId: row.userId,
      // firstName: row.firstName,
      // lastName: row.lastName,
      email: row.email,
      roleId: row.roleId
    };

    const permissionIds = state.permissionsList
      .filter(permission => row.permissions.includes(formatPermissionName(permission.name)))
      .map(permission => permission.permissionId);

    setState(s => ({
      ...s,
      isOpenPermissionModal: true,
      selectedUser: {
        ...selected,
        permissions: permissionIds
      }
    }));
  };

  const handleDelete = row => {
    // todo propmt user delete confirmation, current behavior deletes record by clicking delete button
    console.warn('Deleting:', row);
    try {
      makeRequest({
        method: 'DELETE',
        url: `/management/permissions/delete-user/${row.userId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      });

      setState(s => ({
        ...s,
        usersWithPermissions: s.usersWithPermissions.filter(user => user.userId !== row.userId)
      }));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleAddUser = () =>
    setState(s => ({
      ...s,
      isOpenPermissionModal: true,
      selectedUser: null
    }));

  // memorize the positionsRoles and permissionsList to prevent unnecessary re-renders
  const memoizedPositionsRoles = useMemo(() => state.positionsRoles, [state.positionsRoles]);
  const memoizedPermissionsList = useMemo(() => state.permissionsList, [state.permissionsList]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    getPermissions();
    getPositionsRoles();
    getUserWithPermissions();
  }, [getPermissions, getPositionsRoles, getUserWithPermissions]);

  return (
    <div className="min-h-screen p-6 text-slate-100">
      <SectionIntro
        kicker={t('pageIntro.managementKicker')}
        title={t('nav.usersPermissions')}
        description={t('pageIntro.usersPermissionsDescription')}
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      <ActionCardComponent>
        <input
          type="text"
          placeholder={t('users.searchUserPermission')}
          value={state.searchTerm}
          onChange={e => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className="search-input"
          data-align="left"
        />
        <ButtonComponent onClick={handleAddUser}>
          <PlusIcon className="w-5 h-5 action-icon" />
          {t('users.newUserPermissions')}
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent
        tableData={permissionsTableData}
        defaultSortColumn={permissionsTableData.columns[0]?.key}
      />

      <UserModal
        isOpen={state.isOpenPermissionModal}
        onClose={() => setState(s => ({ ...s, isOpenPermissionModal: false }))}
        onSave={handleCreateUpdateUserPermission}
        positionsRoles={memoizedPositionsRoles}
        permissionsList={memoizedPermissionsList}
        selectedUser={state.selectedUser}
      />
    </div>
  );
}
